import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  OPS_TOOL_BIG_TABLE_CALC_BILL_LOCATION,
  OPS_TOOL_BIG_TABLE_CALC_CLEARING_LOCATION,
  OPS_TOOL_BIG_TABLE_CALC_PRICING_LOCATION,
  OPS_TOOL_BIG_TABLE_MIGRATE_BILL_CLEARING_MAP,
  OPS_TOOL_BIG_TABLE_MIGRATE_CLEARING,
  OPS_TOOL_BIG_TABLE_MIGRATE_PRICING,
  OPS_TOOL_BIG_TABLE_QUERY_BILL_CLEARING_MAP,
  OPS_TOOL_BIG_TABLE_QUERY_CLEARING,
  OPS_TOOL_BIG_TABLE_QUERY_CLEARING_LOG,
  OPS_TOOL_BIG_TABLE_QUERY_PRICING,
  OPS_TOOL_BIG_TABLE_QUERY_PRICING_LOG,
} from '../graphql/opsToolSettlementGraphql';

const { TextArea } = Input;

const MAX_KEYS = 500;

type CommonResultPayload<T> = {
  success: boolean;
  code: string;
  message: string;
  items: T[];
};

type ResultState = {
  columns: ColumnsType<Record<string, unknown>>;
  rows: Record<string, unknown>[];
  label: string;
};

const nowrap = () => ({ className: 'whitespace-nowrap text-center' });

const renderEllipsis = (value: unknown) => {
  const text = value == null || value === '' ? '-' : String(value);
  return (
    <Tooltip title={text}>
      <div className="max-w-[220px] truncate text-center">{text}</div>
    </Tooltip>
  );
};

const renderList = (value: unknown) => {
  const text = Array.isArray(value) && value.length > 0 ? value.join(', ') : '-';
  return renderEllipsis(text);
};

const col = (
  title: string,
  dataIndex: string,
  width: number,
  render: (value: unknown) => React.ReactNode = renderEllipsis,
): ColumnsType<Record<string, unknown>>[number] => ({
  title,
  dataIndex,
  width,
  align: 'center',
  onHeaderCell: nowrap,
  onCell: nowrap,
  render: (value: unknown) => render(value),
});

const MIGRATE_KEY_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('运单号', 'businessId', 200),
  col('主表迁移数', 'mainCount', 110),
  col('日志迁移数', 'logCount', 110),
  col('状态', 'status', 120),
  col('说明', 'message', 260),
];

const MIGRATE_BILL_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('账单id', 'billId', 160),
  col('关联迁移数', 'mapCount', 110),
  col('状态', 'status', 120),
  col('说明', 'message', 260),
];

const CLEARING_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('id', 'id', 160),
  col('运单号', 'businessId', 180),
  col('对端运单号', 'otherBusinessId', 180),
  col('结算客户', 'settlementClientId', 140),
  col('费用项code', 'settlementItemCode', 130),
  col('费用项名称', 'settlementItemName', 150),
  col('金额', 'amount', 120),
  col('本币金额', 'amountHm', 120),
  col('状态', 'status', 90),
  col('计费id', 'pricingId', 160),
  col('业务时间', 'businessTime', 200),
  col('结算时间', 'settlementTime', 200),
  col('清分节点', 'clearingNode', 140),
  col('已删除', 'isDeleted', 90),
  col('重跑任务', 'reRunTaskId', 140),
  col('创建时间', 'createdAt', 200),
  col('更新时间', 'updatedAt', 200),
];

const CLEARING_LOG_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('id', 'id', 160),
  col('运单号', 'businessId', 180),
  col('清分id', 'clearingId', 160),
  col('计费id', 'pricingId', 160),
  col('事件', 'logEvent', 140),
  col('重跑任务', 'reRunTaskId', 140),
  col('操作人', 'operatorId', 140),
  col('描述', 'description', 220),
  col('创建时间', 'createdAt', 200),
  col('更新时间', 'updatedAt', 200),
];

const PRICING_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('id', 'id', 160),
  col('运单号', 'businessId', 180),
  col('对端运单号', 'otherBusinessId', 180),
  col('业务code', 'businessCode', 130),
  col('业务场景code', 'businessScenarioCode', 130),
  col('费用code', 'businessFeeCode', 130),
  col('金额', 'amount', 120),
  col('客户id', 'clientId', 140),
  col('计费时间', 'pricingAt', 200),
  col('业务时间', 'businessAt', 200),
  col('计费节点', 'pricingNode', 140),
  col('计费版本', 'pricingVersion', 130),
  col('错误码', 'pricingErrorCode', 120),
  col('成功', 'isSuccess', 90),
  col('已删除', 'isDeleted', 90),
  col('重推任务', 'rePushTaskId', 140),
  col('提交人', 'submitter', 130),
  col('创建时间', 'createdAt', 200),
  col('更新时间', 'updatedAt', 200),
];

const PRICING_LOG_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('id', 'id', 160),
  col('计费结果id', 'pricingResultId', 160),
  col('运单号', 'businessId', 180),
  col('事件', 'logEvent', 140),
  col('重推任务', 'rePushTask', 140),
  col('操作人', 'operator', 130),
  col('计费版本', 'pricingVersion', 130),
  col('创建时间', 'createdAt', 200),
  col('更新时间', 'updatedAt', 200),
];

const BILL_MAP_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('id', 'id', 160),
  col('账单id', 'billId', 160),
  col('清分id', 'clearingId', 160),
  col('运单号', 'businessId', 180),
  col('费用项', 'settlementFeeItem', 130),
  col('金额', 'amount', 120),
  col('本币金额', 'amountHm', 120),
  col('已删除', 'isDeleted', 90),
  col('创建时间', 'createdAt', 200),
  col('更新时间', 'updatedAt', 200),
];

const SHARD_LOCATION_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('运单号', 'businessId', 200),
  col('索引桶', 'indexBucket', 100),
  col('索引表', 'indexTable', 200),
  col('月份后缀', 'monthSuffixes', 200, renderList),
  col('月份分表', 'monthTables', 260, renderList),
];

const BILL_LOCATION_COLUMNS: ColumnsType<Record<string, unknown>> = [
  col('账单id', 'billId', 160),
  col('索引桶', 'indexBucket', 100),
  col('索引表', 'indexTable', 200),
  col('账期月份', 'monthSuffix', 130),
  col('关联分表', 'mapTable', 220),
  col('说明', 'reason', 220),
];

const parseKeys = (raw: string): string[] =>
  raw
    .split(/[\s,;，；]+/)
    .map((token) => token.trim())
    .filter(Boolean);

// 账单id 为 Long，可能超出 JS 安全整数范围，必须以字符串校验和传输，否则会丢精度
const BILL_ID_PATTERN = /^[1-9]\d{0,18}$/;

const parseBillIds = (raw: string): { ids: string[]; invalid: string[] } => {
  const ids: string[] = [];
  const invalid: string[] = [];
  parseKeys(raw).forEach((token) => {
    if (BILL_ID_PATTERN.test(token)) {
      ids.push(token);
    } else {
      invalid.push(token);
    }
  });
  return { ids, invalid };
};

const TableSplitCompensationTab: React.FC = () => {
  const [businessIdText, setBusinessIdText] = useState('');
  const [billIdText, setBillIdText] = useState('');
  const [clearingResult, setClearingResult] = useState<ResultState | null>(null);
  const [pricingResult, setPricingResult] = useState<ResultState | null>(null);
  const [billResult, setBillResult] = useState<ResultState | null>(null);

  const [migrateClearing, { loading: migrateClearingLoading }] = useMutation(
    OPS_TOOL_BIG_TABLE_MIGRATE_CLEARING,
  );
  const [migratePricing, { loading: migratePricingLoading }] = useMutation(
    OPS_TOOL_BIG_TABLE_MIGRATE_PRICING,
  );
  const [migrateBill, { loading: migrateBillLoading }] = useMutation(
    OPS_TOOL_BIG_TABLE_MIGRATE_BILL_CLEARING_MAP,
  );

  const [queryClearing, { loading: queryClearingLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_QUERY_CLEARING,
    { fetchPolicy: 'network-only' },
  );
  const [queryClearingLog, { loading: queryClearingLogLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_QUERY_CLEARING_LOG,
    { fetchPolicy: 'network-only' },
  );
  const [calcClearing, { loading: calcClearingLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_CALC_CLEARING_LOCATION,
    { fetchPolicy: 'network-only' },
  );
  const [queryPricing, { loading: queryPricingLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_QUERY_PRICING,
    { fetchPolicy: 'network-only' },
  );
  const [queryPricingLog, { loading: queryPricingLogLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_QUERY_PRICING_LOG,
    { fetchPolicy: 'network-only' },
  );
  const [calcPricing, { loading: calcPricingLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_CALC_PRICING_LOCATION,
    { fetchPolicy: 'network-only' },
  );
  const [queryBill, { loading: queryBillLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_QUERY_BILL_CLEARING_MAP,
    { fetchPolicy: 'network-only' },
  );
  const [calcBill, { loading: calcBillLoading }] = useLazyQuery(
    OPS_TOOL_BIG_TABLE_CALC_BILL_LOCATION,
    { fetchPolicy: 'network-only' },
  );

  const businessIds = useMemo(() => parseKeys(businessIdText), [businessIdText]);
  const parsedBillIds = useMemo(() => parseBillIds(billIdText), [billIdText]);

  const requireBusinessIds = (): string[] | null => {
    if (businessIds.length === 0) {
      message.error('请先输入运单号');
      return null;
    }
    if (businessIds.length > MAX_KEYS) {
      message.error(`单次最多处理 ${MAX_KEYS} 个运单号，当前 ${businessIds.length}`);
      return null;
    }
    return businessIds;
  };

  const requireBillIds = (): string[] | null => {
    if (parsedBillIds.invalid.length > 0) {
      message.error(`存在非法账单id：${parsedBillIds.invalid.slice(0, 5).join(', ')}`);
      return null;
    }
    if (parsedBillIds.ids.length === 0) {
      message.error('请先输入账单id');
      return null;
    }
    if (parsedBillIds.ids.length > MAX_KEYS) {
      message.error(`单次最多处理 ${MAX_KEYS} 个账单id，当前 ${parsedBillIds.ids.length}`);
      return null;
    }
    return parsedBillIds.ids;
  };

  const applyResult = (
    payload: CommonResultPayload<Record<string, unknown>> | undefined,
    columns: ColumnsType<Record<string, unknown>>,
    label: string,
    setResult: (state: ResultState) => void,
  ) => {
    if (!payload) {
      message.error(`${label}无返回`);
      return;
    }
    const rows = (payload.items ?? []).map((item, index) => ({ ...item, _rowKey: index }));
    if (payload.success) {
      message.success(`${label}成功，共 ${rows.length} 条`);
    } else {
      message.error(payload.message || `${label}失败（code=${payload.code}）`);
    }
    setResult({ columns, rows, label });
  };

  const confirmMigrate = (title: string, content: string, onOk: () => Promise<void>) => {
    Modal.confirm({
      title,
      content,
      okText: '确认执行',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk,
    });
  };

  const runMigrateClearing = () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    confirmMigrate('确认迁移清分分表', `将按 ${ids.length} 个运单号迁移清分主表及日志到新分表。`, async () => {
      const { data } = await migrateClearing({ variables: { businessIds: ids } });
      applyResult(data?.opsToolBigTableMigrateClearing, MIGRATE_KEY_COLUMNS, '迁移清分', setClearingResult);
    });
  };

  const runQueryClearing = async () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    const { data } = await queryClearing({ variables: { businessIds: ids } });
    applyResult(data?.opsToolBigTableQueryClearing, CLEARING_COLUMNS, '查询清分', setClearingResult);
  };

  const runQueryClearingLog = async () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    const { data } = await queryClearingLog({ variables: { businessIds: ids } });
    applyResult(data?.opsToolBigTableQueryClearingLog, CLEARING_LOG_COLUMNS, '查询清分日志', setClearingResult);
  };

  const runCalcClearing = async () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    const { data } = await calcClearing({ variables: { businessIds: ids } });
    applyResult(data?.opsToolBigTableCalcClearingLocation, SHARD_LOCATION_COLUMNS, '计算清分落点', setClearingResult);
  };

  const runMigratePricing = () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    confirmMigrate('确认迁移计费分表', `将按 ${ids.length} 个运单号迁移计费主表及日志到新分表。`, async () => {
      const { data } = await migratePricing({ variables: { businessIds: ids } });
      applyResult(data?.opsToolBigTableMigratePricing, MIGRATE_KEY_COLUMNS, '迁移计费', setPricingResult);
    });
  };

  const runQueryPricing = async () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    const { data } = await queryPricing({ variables: { businessIds: ids } });
    applyResult(data?.opsToolBigTableQueryPricing, PRICING_COLUMNS, '查询计费', setPricingResult);
  };

  const runQueryPricingLog = async () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    const { data } = await queryPricingLog({ variables: { businessIds: ids } });
    applyResult(data?.opsToolBigTableQueryPricingLog, PRICING_LOG_COLUMNS, '查询计费日志', setPricingResult);
  };

  const runCalcPricing = async () => {
    const ids = requireBusinessIds();
    if (!ids) return;
    const { data } = await calcPricing({ variables: { businessIds: ids } });
    applyResult(data?.opsToolBigTableCalcPricingLocation, SHARD_LOCATION_COLUMNS, '计算计费落点', setPricingResult);
  };

  const runMigrateBill = () => {
    const ids = requireBillIds();
    if (!ids) return;
    confirmMigrate('确认迁移账单关联分表', `将按 ${ids.length} 个账单id迁移账单关联表到新分表。`, async () => {
      const { data } = await migrateBill({ variables: { billIds: ids } });
      applyResult(data?.opsToolBigTableMigrateBillClearingMap, MIGRATE_BILL_COLUMNS, '迁移账单关联', setBillResult);
    });
  };

  const runQueryBill = async () => {
    const ids = requireBillIds();
    if (!ids) return;
    const { data } = await queryBill({ variables: { billIds: ids } });
    applyResult(data?.opsToolBigTableQueryBillClearingMap, BILL_MAP_COLUMNS, '查询账单关联', setBillResult);
  };

  const runCalcBill = async () => {
    const ids = requireBillIds();
    if (!ids) return;
    const { data } = await calcBill({ variables: { billIds: ids } });
    applyResult(data?.opsToolBigTableCalcBillLocation, BILL_LOCATION_COLUMNS, '计算账单落点', setBillResult);
  };

  const renderResult = (result: ResultState | null) => {
    if (!result) {
      return <Typography.Text type="secondary">执行操作后在此展示结果</Typography.Text>;
    }
    return (
      <Card size="small" title={`结果：${result.label}（${result.rows.length} 条）`}>
        <Table<Record<string, unknown>>
          rowKey="_rowKey"
          size="small"
          columns={result.columns}
          dataSource={result.rows}
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
          locale={{ emptyText: '暂无数据' }}
        />
      </Card>
    );
  };

  const businessInput = (
    <Card size="small" title="运单号列表" className="mb-4">
      <Space direction="vertical" className="w-full">
        <TextArea
          value={businessIdText}
          onChange={(event) => setBusinessIdText(event.target.value)}
          placeholder="输入运单号，支持换行、逗号、空格分隔，单次最多 500 个"
          autoSize={{ minRows: 3, maxRows: 8 }}
        />
        <Typography.Text type="secondary">
          已解析 <Tag color={businessIds.length > MAX_KEYS ? 'red' : 'blue'}>{businessIds.length}</Tag> 个运单号
        </Typography.Text>
      </Space>
    </Card>
  );

  const billInput = (
    <Card size="small" title="账单id列表" className="mb-4">
      <Space direction="vertical" className="w-full">
        <TextArea
          value={billIdText}
          onChange={(event) => setBillIdText(event.target.value)}
          placeholder="输入账单id（正整数），支持换行、逗号、空格分隔，单次最多 500 个"
          autoSize={{ minRows: 3, maxRows: 8 }}
        />
        <Typography.Text type="secondary">
          已解析 <Tag color={parsedBillIds.ids.length > MAX_KEYS ? 'red' : 'blue'}>{parsedBillIds.ids.length}</Tag> 个账单id
          {parsedBillIds.invalid.length > 0 && (
            <Tag color="red" className="ml-2">
              非法 {parsedBillIds.invalid.length} 个
            </Tag>
          )}
        </Typography.Text>
      </Space>
    </Card>
  );

  const tabItems = [
    {
      key: 'clearing',
      label: '清分',
      children: (
        <div>
          {businessInput}
          <Space wrap className="mb-4">
            <Button danger loading={migrateClearingLoading} onClick={runMigrateClearing}>
              迁移清分分表
            </Button>
            <Button type="primary" loading={queryClearingLoading} onClick={runQueryClearing}>
              查询清分记录
            </Button>
            <Button loading={queryClearingLogLoading} onClick={runQueryClearingLog}>
              查询清分日志
            </Button>
            <Button loading={calcClearingLoading} onClick={runCalcClearing}>
              计算清分落点
            </Button>
          </Space>
          {renderResult(clearingResult)}
        </div>
      ),
    },
    {
      key: 'pricing',
      label: '计费',
      children: (
        <div>
          {businessInput}
          <Space wrap className="mb-4">
            <Button danger loading={migratePricingLoading} onClick={runMigratePricing}>
              迁移计费分表
            </Button>
            <Button type="primary" loading={queryPricingLoading} onClick={runQueryPricing}>
              查询计费记录
            </Button>
            <Button loading={queryPricingLogLoading} onClick={runQueryPricingLog}>
              查询计费日志
            </Button>
            <Button loading={calcPricingLoading} onClick={runCalcPricing}>
              计算计费落点
            </Button>
          </Space>
          {renderResult(pricingResult)}
        </div>
      ),
    },
    {
      key: 'bill',
      label: '账单关联',
      children: (
        <div>
          {billInput}
          <Space wrap className="mb-4">
            <Button danger loading={migrateBillLoading} onClick={runMigrateBill}>
              迁移账单关联分表
            </Button>
            <Button type="primary" loading={queryBillLoading} onClick={runQueryBill}>
              查询账单关联记录
            </Button>
            <Button loading={calcBillLoading} onClick={runCalcBill}>
              计算账单落点
            </Button>
          </Space>
          {renderResult(billResult)}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Alert
        type="warning"
        showIcon
        message="大表拆分补偿运维操作"
        description="迁移操作会将老单库数据写入新分表，属高风险写操作，请确认运单号 / 账单id 无误后再执行。查询与落点计算为只读操作。单次最多处理 500 条。"
      />
      <Tabs items={tabItems} />
    </div>
  );
};

export default TableSplitCompensationTab;
